import React, { useState } from 'react';
import { MessageCircle, Send, User } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { formatDistanceToNow } from 'date-fns';
import { Comment } from '../types/todo';
import './InlineComments.css';

interface InlineCommentsProps {
  comments: Comment[];
  onAddComment?: (text: string) => Promise<void>;
}

export const InlineComments: React.FC<InlineCommentsProps> = ({ 
  comments = [], 
  onAddComment
}) => {
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { currentUser } = useUser();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || isSubmitting || !onAddComment) return;

    setIsSubmitting(true);
    try {
      await onAddComment(newComment.trim());
      setNewComment('');
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSubmit(e as React.FormEvent);
    }
  };

  const formatTimeAgo = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'Unknown time';
    }
  };

  const getInitials = (email: string) => {
    return email.split('@')[0].slice(0, 2).toUpperCase();
  };

  return (
    <div className="inline-comments">
      {/* Comments List */}
      <div className="inline-comments-list">
        {comments.length === 0 ? (
          <div className="inline-no-comments">
            <MessageCircle size={24} />
            <p>No comments yet</p>
            <small>Be the first to add a comment!</small>
          </div>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="inline-comment-item">
              <div className="inline-comment-avatar">
                <User size={16} />
                <span className="inline-comment-initials">
                  {getInitials(comment.authorEmail)}
                </span>
              </div>
              <div className="inline-comment-content">
                <div className="inline-comment-header">
                  <span className="inline-comment-author">
                    {comment.authorEmail.split('@')[0]}
                  </span>
                  <span className="inline-comment-time">
                    {formatTimeAgo(comment.createdAt)}
                  </span>
                </div>
                <div className="inline-comment-text">
                  {comment.content}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Comment Form */}
      {currentUser && (
        <form className="inline-add-comment-form" onSubmit={handleSubmit}>
          <div className="inline-comment-input-container">
            <div className="inline-comment-avatar inline-comment-avatar-current">
              <User size={16} />
              <span className="inline-comment-initials">
                {getInitials(currentUser.email)}
              </span>
            </div>
            <div className="inline-comment-input-wrapper">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Add a comment..."
                className="inline-comment-input"
                rows={3}
                disabled={isSubmitting}
                maxLength={1000}
              />
              <div className="inline-comment-actions">
                <small className="inline-comment-hint">
                  {navigator.platform.includes('Mac') ? 'Cmd' : 'Ctrl'}+Enter to send
                </small>
                <button
                  type="submit"
                  className="inline-comment-submit"
                  disabled={!newComment.trim() || isSubmitting}
                >
                  <Send size={14} />
                  {isSubmitting ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};
